import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { QrBatchResponse, SchemaDoc } from '../types';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--card-bg)',
  color: 'var(--text-color)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

export function BatchQRPage() {
  const { industryId, schemaId } = useParams<{ industryId: string; schemaId: string }>();
  const [schema, setSchema] = useState<SchemaDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ttlSeconds, setTtlSeconds] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<QrBatchResponse | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  useEffect(() => {
    if (!industryId || !schemaId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await apiCall<SchemaDoc>(
          `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
        );
        if (cancelled) return;
        setSchema(s);
        if (s.defaultCodeTtlDays && s.defaultCodeTtlDays > 0) {
          setTtlSeconds(s.defaultCodeTtlDays * 86400);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId, schemaId]);

  const expiresAtPreview = useMemo(() => {
    if (typeof ttlSeconds !== 'number' || ttlSeconds <= 0) return null;
    return new Date(Date.now() + ttlSeconds * 1000);
  }, [ttlSeconds]);

  async function generate() {
    if (!industryId || !schemaId) return;
    setGenError(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      if (typeof ttlSeconds === 'number' && ttlSeconds > 0) body.ttlSeconds = ttlSeconds;
      const r = await apiCall<QrBatchResponse>(
        `/api/qr/batch?industryId=${encodeURIComponent(industryId)}&schemaId=${encodeURIComponent(schemaId)}`,
        { method: 'POST', body },
      );
      setResult(r);
    } catch (e) {
      setGenError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!industryId || !schemaId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing route parameters.</p>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/industries/${industryId}/schemas/${schemaId}/participants`}
          style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
        >
          ← Participants
        </Link>
        <h1 style={{ margin: '4px 0 0' }}>Batch QR generation</h1>
        {schema && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 0' }}>
            {schema.name} · v{schema.version}
          </p>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : !schema ? null : (
        <div
          style={{
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            padding: 20,
            borderRadius: 8,
            maxWidth: 520,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Generate codes for all active participants</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            We will create a fresh QR code for every active participant on this schema and bundle them into a single
            downloadable ZIP. The download link is signed and time-limited.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>TTL per code (seconds)</label>
            <input
              type="number"
              min={0}
              value={ttlSeconds}
              onChange={(e) => setTtlSeconds(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
              style={inputStyle}
              placeholder="(no expiry)"
            />
            <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
              {schema.defaultCodeTtlDays
                ? `Pre-filled from schema default (${schema.defaultCodeTtlDays} days).`
                : 'No schema default; leave blank for no expiry.'}{' '}
              {expiresAtPreview && `Codes will stop working on ${expiresAtPreview.toLocaleString()}.`}
            </p>
          </div>

          {genError && <div className="form-error">{genError}</div>}

          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void generate()}
            disabled={busy}
          >
            {busy ? 'Generating batch…' : 'Generate batch'}
          </button>

          {result && (
            <div
              style={{
                marginTop: 20,
                padding: 16,
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid var(--success-color, #10b981)',
                borderRadius: 6,
              }}
            >
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Generated {result.count} code{result.count === 1 ? '' : 's'}.</strong>
              </p>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                Download link expires {new Date(result.expiresAt).toLocaleString()}.
              </p>
              <a
                href={result.sasUrl}
                className="btn btn--primary"
                style={{ textDecoration: 'none' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download ZIP
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
